import { useEffect, useState, useMemo, useRef } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import {
  SlidersHorizontal,
  ChevronDown,
  Grid,
  LayoutList,
  Award,
  Sparkles,
  Layers,
  Leaf,
  Search,
  X,
  RotateCcw,
  PackageSearch,
  ArrowRight,
  ArrowLeft,
  Check,
} from "lucide-react";
import ProductCard from "../components/ProductCard";
import * as productsApi from "../api/productsApi";
import { API_BASE_URL } from "../config";
import { useLanguage } from "../context/LanguageContext";
import { resolveImageUrl } from "../utils/images";
import { updateSEO } from "../utils/seo";
import "./Shop.css";

const SORT_KEYS = [
  { key: "collection", labelKey: "shop.sort.collection" },
  { key: "bestselling", labelKey: "shop.sort.bestselling" },
  { key: "newest", labelKey: "shop.sort.newest" },
  { key: "priceAsc", labelKey: "shop.sort.priceAsc" },
  { key: "priceDesc", labelKey: "shop.sort.priceDesc" },
];

const PRICE_PRESETS = [
  { labelKey: "shop.priceUnder500", min: "", max: "500" },
  { labelKey: "shop.price500to1500", min: "500", max: "1500" },
  { labelKey: "shop.priceOver1500", min: "1500", max: "" },
];

function normalizeApiProduct(p) {
  const sortedImages = (p.productImages || [])
    .slice()
    .sort((a, b) => (a.displayOrder || 0) - (b.displayOrder || 0));
  return {
    id: p.id,
    name: p.name,
    arabicName: p.arabicName,
    arabicDescription: p.arabicDescription,
    description: p.description,
    price: Number(p.basePrice),
    imageUrl: p.thumbnailImage || sortedImages[0]?.url,
    color: p.colorVariants?.[0]?.colorHex || "#d8cfc0",
    borderColor: "#c9a96e",
    badge: null,
    unitsSold: p.unitsSold || 0,
    source: "api",
  };
}

export function findMatchingCategory(target, categories) {
  if (!target || !Array.isArray(categories) || categories.length === 0) return null;
  const decoded = decodeURIComponent(target).trim();
  if (!decoded) return null;
  const lower = decoded.toLowerCase();

  // 1. Direct ID match (Guid or string)
  const byId = categories.find(
    (c) => c.id && String(c.id).toLowerCase() === lower && c.id !== "all"
  );
  if (byId) return byId;

  // 2. Exact Name match (case-insensitive)
  const byName = categories.find(
    (c) => c.name && c.name.toLowerCase() === lower && c.id !== "all"
  );
  if (byName) return byName;

  // 3. Exact Arabic Name match
  const byArabic = categories.find(
    (c) => c.arabicName && c.arabicName.trim().toLowerCase() === lower && c.id !== "all"
  );
  if (byArabic) return byArabic;

  // 4. Normalized alphanumeric slug match (e.g. "bath-robes" -> "bathrobes")
  const normTarget = lower.replace(/[^a-z0-9]/g, "");
  if (normTarget) {
    const byNorm = categories.find((c) => {
      if (c.id === "all" || !c.name) return false;
      const normName = c.name.toLowerCase().replace(/[^a-z0-9]/g, "");
      return normName === normTarget;
    });
    if (byNorm) return byNorm;
  }

  // 5. Keyword & Semantic Matching (supports Arabic & English terms)
  // Towels / بشاكير / مناشف / فوط
  if (
    lower.includes("towel") ||
    lower.includes("مناشف") ||
    lower.includes("منشف") ||
    lower.includes("بشكير") ||
    lower.includes("بشاكير") ||
    lower.includes("فوط")
  ) {
    const towelCat = categories.find(
      (c) =>
        c.id !== "all" &&
        ((c.name && c.name.toLowerCase().includes("towel")) ||
          (c.arabicName &&
            (c.arabicName.includes("منشف") ||
              c.arabicName.includes("مناشف") ||
              c.arabicName.includes("بشكير"))))
    );
    if (towelCat) return towelCat;
  }

  // Bathrobes / أرواب / روب / برانص / برنس
  if (
    lower.includes("robe") ||
    lower.includes("bathrobe") ||
    lower.includes("أرواب") ||
    lower.includes("ارواب") ||
    lower.includes("روب") ||
    lower.includes("برنس") ||
    lower.includes("برانص") ||
    lower.includes("برانس")
  ) {
    const robeCat = categories.find(
      (c) =>
        c.id !== "all" &&
        ((c.name &&
          (c.name.toLowerCase().includes("robe") ||
            c.name.toLowerCase().includes("bathrobe"))) ||
          (c.arabicName &&
            (c.arabicName.includes("روب") ||
              c.arabicName.includes("أرواب") ||
              c.arabicName.includes("ارواب") ||
              c.arabicName.includes("برنس") ||
              c.arabicName.includes("برانص") ||
              c.arabicName.includes("برانس"))))
    );
    if (robeCat) return robeCat;
  }

  // Bridal / عرائس / عرايس / جهاز العروسة
  if (
    lower.includes("bride") ||
    lower.includes("bridal") ||
    lower.includes("wedding") ||
    lower.includes("عروس") ||
    lower.includes("عرائس") ||
    lower.includes("عرايس") ||
    lower.includes("جهاز")
  ) {
    const bridalCat = categories.find(
      (c) =>
        c.id !== "all" &&
        ((c.name &&
          (c.name.toLowerCase().includes("bride") ||
            c.name.toLowerCase().includes("bridal") ||
            c.name.toLowerCase().includes("wedding"))) ||
          (c.arabicName &&
            (c.arabicName.includes("عروس") ||
              c.arabicName.includes("عرايس") ||
              c.arabicName.includes("عرائس") ||
              c.arabicName.includes("جهاز"))))
    );
    if (bridalCat) return bridalCat;
  }

  // Baby / أطفال / مواليد
  if (
    lower.includes("baby") ||
    lower.includes("kid") ||
    lower.includes("child") ||
    lower.includes("طفل") ||
    lower.includes("أطفال") ||
    lower.includes("اطفال") ||
    lower.includes("مواليد")
  ) {
    const babyCat = categories.find(
      (c) =>
        c.id !== "all" &&
        ((c.name &&
          (c.name.toLowerCase().includes("baby") || c.name.toLowerCase().includes("kid"))) ||
          (c.arabicName &&
            (c.arabicName.includes("طفل") ||
              c.arabicName.includes("أطفال") ||
              c.arabicName.includes("مواليد"))))
    );
    if (babyCat) return babyCat;
  }

  // 6. Substring match
  const bySub = categories.find(
    (c) =>
      c.id !== "all" &&
      ((c.name &&
        (c.name.toLowerCase().includes(lower) || lower.includes(c.name.toLowerCase()))) ||
        (c.arabicName &&
          (c.arabicName.includes(decoded) || decoded.includes(c.arabicName))))
  );
  if (bySub) return bySub;

  return null;
}

export default function Shop({ onAddToCart }) {
  const { id: collectionSlug } = useParams();
  const { t, isRTL, language } = useLanguage();

  const [searchParams] = useSearchParams();
  const queryCollection =
    searchParams.get("collection") ||
    searchParams.get("category") ||
    searchParams.get("id");
  const targetSlug = (collectionSlug || queryCollection || "").trim();

  const initialSort = searchParams.get("sort");
  const validSorts = ["collection", "bestselling", "newest", "priceAsc", "priceDesc"];

  // Filters state
  const [activeCategory, setActiveCategory] = useState("All");
  const [searchQuery, setSearchQuery] = useState("");
  const [priceRange, setPriceRange] = useState({ min: "", max: "" });
  const [sortBy, setSortBy] = useState(
    initialSort && validSorts.includes(initialSort) ? initialSort : "collection"
  );

  // UI state
  const [showSort, setShowSort] = useState(false);
  const [showFilterDrawer, setShowFilterDrawer] = useState(false);
  const [gridCols, setGridCols] = useState(4);
  const sortRef = useRef(null);

  // Close sort dropdown when clicking outside or pressing Escape
  useEffect(() => {
    if (!showSort) return;

    function handleClickOutside(event) {
      if (sortRef.current && !sortRef.current.contains(event.target)) {
        setShowSort(false);
      }
    }

    function handleKeyDown(event) {
      if (event.key === "Escape") {
        setShowSort(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("touchstart", handleClickOutside);
    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("touchstart", handleClickOutside);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [showSort]);

  // Data state
  const [apiProducts, setApiProducts] = useState(null);
  const [apiError, setApiError] = useState(false);
  const [filterCategories, setFilterCategories] = useState([{ name: "All", id: "all" }]);
  const [categoryMap, setCategoryMap] = useState(() => new Map());

  // Load collections
  useEffect(() => {
    let cancelled = false;
    productsApi
      .fetchCollections()
      .then((data) => {
        if (!cancelled) {
          setFilterCategories([
            { name: "All", id: "all" },
            ...(Array.isArray(data) ? data : []),
          ]);
        }
      })
      .catch(console.error);
    return () => {
      cancelled = true;
    };
  }, []);

  // Load product-to-category mapping for Category-Clustered Flat Grid and deduplication
  useEffect(() => {
    let cancelled = false;
    productsApi
      .fetchProductCategoriesMap()
      .then((map) => {
        if (!cancelled && map) {
          setCategoryMap(map);
        }
      })
      .catch((err) => {
        console.warn("Could not load product categories map:", err);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Sync category with URL slug or query parameter
  useEffect(() => {
    if (!targetSlug) {
      setActiveCategory("All");
      return;
    }
    const cat = findMatchingCategory(targetSlug, filterCategories);
    if (cat) {
      setActiveCategory(cat.name);
    } else if (filterCategories.length > 1) {
      // If categories finished loading and none matched, default to All
      setActiveCategory("All");
    }
  }, [targetSlug, filterCategories]);

  // Sync sort with URL search param
  useEffect(() => {
    const s = searchParams.get("sort");
    if (s && validSorts.includes(s)) {
      setSortBy(s);
    }
  }, [searchParams]);

  // Dynamic SEO title, meta description & keywords
  useEffect(() => {
    let catTitle = "";
    if (activeCategory && activeCategory !== "All") {
      const catObj = filterCategories.find(
        (c) => c.name === activeCategory || c.id === activeCategory
      );
      catTitle = isRTL && catObj?.arabicName ? catObj.arabicName : activeCategory;
    }

    let pageTitle = "";
    let pageDesc = "";
    if (catTitle) {
      pageTitle = `${catTitle} | ${isRTL ? "تيري لاند للمفروشات والوبريات" : "Terry Land Luxury Textiles"}`;
      pageDesc = isRTL
        ? `تسوق تشكيلة ${catTitle} الفاخرة من تيري لاند بأفضل خامات القطن والمايكروفايبر والوجهين مع شحن سريع لجميع المحافظات والدفع عند الاستلام.`
        : `Explore our luxury ${catTitle} collection by Terry Land crafted from high-quality cotton & microfiber with fast delivery across Egypt.`;
    } else if (searchQuery.trim()) {
      pageTitle = `${isRTL ? "نتائج البحث عن:" : "Search for:"} ${searchQuery.trim()} | ${isRTL ? "تيري لاند" : "Terry Land"}`;
      pageDesc = isRTL
        ? `نتائج البحث عن ${searchQuery.trim()} في متجر تيري لاند لأفخم برانص الحمام والفوط والبشاكير وتجهيزات العرائس.`
        : `Search results for ${searchQuery.trim()} at Terry Land luxury textiles.`;
    } else {
      pageTitle =
        t("shop.metaTitle") ||
        (isRTL
          ? "المتجر | برانص حمام وفوط وبشاكير ومفروشات عرائس — تيري لاند"
          : "The Shop | Bathrobes, Towels & Bridal Collections — Terry Land");
      pageDesc = isRTL
        ? "تسوق تشكيلات تيري لاند الفاخرة: برانص حمام، فوط وبشاكير فندقية، وتجهيزات جهاز العروسة بخامات متطورة تجمع بين نعومة القطن ومتانة المايكروفايبر وخامة الوجهين."
        : "Shop Terry Land's luxury bathrobes, hotel towels, bath sheets, and bridal sets crafted from cotton and ultra-soft microfiber blends.";
    }

    updateSEO({
      title: pageTitle,
      description: pageDesc,
      canonical: "https://terryland.net/shop",
    });
  }, [activeCategory, filterCategories, searchQuery, isRTL, t]);

  // Load products when category changes
  useEffect(() => {
    let cancelled = false;
    setApiProducts(null);
    productsApi
      .fetchProducts(activeCategory === "All" ? null : activeCategory)
      .then((data) => {
        if (!cancelled) {
          setApiProducts(Array.isArray(data) ? data : []);
          setApiError(false);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setApiProducts([]);
          setApiError(true);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [activeCategory]);

  // Find currently active collection object
  const currentCatObj = useMemo(() => {
    return filterCategories.find((c) => c.name === activeCategory);
  }, [filterCategories, activeCategory]);

  // Compute localized collection title and description
  const activeCategoryTitle = useMemo(() => {
    if (activeCategory === "All") return t("shop.all");
    return (isRTL || language === "ar") && currentCatObj?.arabicName?.trim()
      ? currentCatObj.arabicName.trim()
      : activeCategory;
  }, [activeCategory, currentCatObj, isRTL, language, t]);

  const activeCategoryDesc = useMemo(() => {
    if (activeCategory === "All") return t("shop.subtitle");
    return (isRTL || language === "ar") && currentCatObj?.arabicDescription?.trim()
      ? currentCatObj.arabicDescription.trim()
      : currentCatObj?.description?.trim() || t("shop.subtitle");
  }, [activeCategory, currentCatObj, isRTL, language, t]);

  // Filter & Sort Pipeline
  const filteredAndSorted = useMemo(() => {
    if (!apiProducts) return [];

    let list = apiProducts.map(normalizeApiProduct);

    // 1. Deduplication: ensure if there is a product in multiple collections, it never appears twice
    const seen = new Set();
    list = list.filter((p) => {
      if (!p?.id) return false;
      const key = String(p.id).trim().toLowerCase();
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });

    // 2. Keyword search filter
    const q = searchQuery.trim().toLowerCase();
    if (q) {
      list = list.filter((p) => {
        const nameMatch = p.name && p.name.toLowerCase().includes(q);
        const arNameMatch = p.arabicName && p.arabicName.toLowerCase().includes(q);
        const descMatch = p.description && p.description.toLowerCase().includes(q);
        const arDescMatch = p.arabicDescription && p.arabicDescription.toLowerCase().includes(q);
        return nameMatch || arNameMatch || descMatch || arDescMatch;
      });
    }

    // 3. Price range filter
    const min = parseFloat(priceRange.min);
    const max = parseFloat(priceRange.max);
    if (!isNaN(min)) {
      list = list.filter((p) => p.price >= min);
    }
    if (!isNaN(max)) {
      list = list.filter((p) => p.price <= max);
    }

    // 4. Sorting (Category-Clustered Flat Grid as default)
    list.sort((a, b) => {
      if (sortBy === "bestselling") return (b.unitsSold || 0) - (a.unitsSold || 0);
      if (sortBy === "priceAsc") return a.price - b.price;
      if (sortBy === "priceDesc") return b.price - a.price;
      if (sortBy === "newest") return 0; // Pure API order / chronological

      // Category-Clustered Flat Grid (ASOS, Uniqlo, Mango)
      // Clustered by collection order number (displayOrder)
      const idA = String(a.id).trim().toLowerCase();
      const idB = String(b.id).trim().toLowerCase();
      const infoA = categoryMap.get(idA);
      const infoB = categoryMap.get(idB);

      const orderA = typeof infoA?.displayOrder === "number" ? infoA.displayOrder : 9999;
      const orderB = typeof infoB?.displayOrder === "number" ? infoB.displayOrder : 9999;

      // Primary: Category DisplayOrder (ascending: 1, 2, 3...)
      if (orderA !== orderB) {
        return orderA - orderB;
      }

      // Secondary: Collection Name (keeps products in the same collection together)
      const colA = infoA?.categoryName || "";
      const colB = infoB?.categoryName || "";
      if (colA !== colB) {
        return colA.localeCompare(colB);
      }

      // Tertiary: Best-selling within the collection cluster
      const unitsDiff = (b.unitsSold || 0) - (a.unitsSold || 0);
      if (unitsDiff !== 0) return unitsDiff;

      // Stable tie-breaker: Product Name
      return String(a.name || "").localeCompare(String(b.name || ""));
    });

    return list;
  }, [apiProducts, searchQuery, priceRange, sortBy, categoryMap]);

  // Active filters count
  const activeFiltersCount = useMemo(() => {
    let count = 0;
    if (activeCategory !== "All") count += 1;
    if (searchQuery.trim()) count += 1;
    if (priceRange.min || priceRange.max) count += 1;
    return count;
  }, [activeCategory, searchQuery, priceRange]);

  // Reset all filters
  const resetAllFilters = () => {
    setActiveCategory("All");
    setSearchQuery("");
    setPriceRange({ min: "", max: "" });
    setSortBy("collection");
  };

  const currentSort = SORT_KEYS.find((s) => s.key === sortBy) || SORT_KEYS[0];
  const collectionCover =
    activeCategory !== "All" && currentCatObj?.imageUrl
      ? resolveImageUrl(currentCatObj.imageUrl)
      : null;

  const headerRef = useRef(null);

  const handleHeaderMouseMove = (e) => {
    if (!headerRef.current) return;
    const rect = headerRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    headerRef.current.style.setProperty("--spot-x", `${x}px`);
    headerRef.current.style.setProperty("--spot-y", `${y}px`);
    headerRef.current.style.setProperty("--spot-opacity", "1");
  };

  const handleHeaderMouseLeave = () => {
    if (!headerRef.current) return;
    headerRef.current.style.setProperty("--spot-opacity", "0");
  };

  return (
    <div className="shop-page">
      {/* ── Dynamic Hero Banner ── */}
      <section
        ref={headerRef}
        className={`shop-page__header ${collectionCover ? "shop-page__header--has-cover" : ""}`}
        style={
          collectionCover
            ? {
                backgroundImage: `url(${collectionCover})`,
                backgroundSize: "cover",
                backgroundPosition: "center center",
                backgroundRepeat: "no-repeat",
              }
            : undefined
        }
        onMouseMove={handleHeaderMouseMove}
        onMouseLeave={handleHeaderMouseLeave}
      >
        {/* Animated & Interactive Lighting Layers only when no cover image is active */}
        {!collectionCover && (
          <>
            <div className="shop-page__spotlight-ambient" aria-hidden="true" />
            <div className="shop-page__spotlight-cursor" aria-hidden="true" />
          </>
        )}
        <div className="shop-page__header-overlay" />
        <div className="shop-page__header-bottom-line" aria-hidden="true" />

        <div className="container shop-page__header-content">
          <nav className="breadcrumb" aria-label="Breadcrumbs">
            <Link to="/">{t("nav.home")}</Link>
            <span className="breadcrumb__sep">/</span>
            <Link to="/shop">{t("nav.shop")}</Link>
            {activeCategory !== "All" && (
              <>
                <span className="breadcrumb__sep">/</span>
                <span className="breadcrumb__current">{activeCategoryTitle}</span>
              </>
            )}
          </nav>


          <h1 className="shop-page__title">{activeCategoryTitle}</h1>

          {activeCategoryDesc && (
            <p className="shop-page__subtitle">{activeCategoryDesc}</p>
          )}

          {activeCategory !== "All" && (
            <div className="shop-page__header-actions">
              <button
                type="button"
                className="btn-ghost shop-page__view-all-btn"
                onClick={() => setActiveCategory("All")}
              >
                <ArrowLeft size={14} />
                <span>{t("shop.all")}</span>
              </button>
            </div>
          )}
        </div>
      </section>

      {/* ── Features Bar ── */}
      <div className="features-bar">
        <div className="container features-bar__inner">
          {[
            { icon: Award, title: t("feature.premium"), sub: t("feature.premiumSub") },
            { icon: Sparkles, title: t("feature.ultraSoft"), sub: t("feature.ultraSoftSub") },
            { icon: Layers, title: t("feature.timeless"), sub: t("feature.timelessSub") },
            { icon: Leaf, title: t("feature.sustainable"), sub: t("feature.sustainableSub") },
          ].map((f) => (
            <div key={f.title} className="features-bar__item">
              <f.icon size={20} strokeWidth={1.5} />
              <div>
                <span className="features-bar__title">{f.title}</span>
                <span className="features-bar__sub">{f.sub}</span>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* ── Main Shop Body ── */}
      <div className="container shop-page__body">
        {apiError && (
          <p className="shop-api-note">
            Showing offline catalogue — connect the API at <code>{API_BASE_URL}</code> for live
            products.
          </p>
        )}

        {/* ── Search & Filter Toolbar ── */}
        <div className="shop-toolbar">
          {/* Left: Filter Toggle & Search Bar */}
          <div className="shop-toolbar__left">
            <button
              type="button"
              className={`shop-toolbar__filter-btn ${activeFiltersCount > 0 ? "has-filters" : ""}`}
              onClick={() => setShowFilterDrawer(true)}
              aria-label={t("action.filter")}
            >
              <SlidersHorizontal size={15} />
              <span>{t("action.filter")}</span>
              {activeFiltersCount > 0 && (
                <span className="shop-toolbar__filter-count">{activeFiltersCount}</span>
              )}
            </button>

            {/* In-page live search box */}
            <div className="shop-search-box">
              <Search size={15} className="shop-search-icon" />
              <input
                type="text"
                placeholder={t("shop.searchPlaceholder")}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="shop-search-input"
              />
              {searchQuery && (
                <button
                  type="button"
                  className="shop-search-clear"
                  onClick={() => setSearchQuery("")}
                  aria-label="Clear search"
                >
                  <X size={13} />
                </button>
              )}
            </div>
          </div>

          {/* Right: Results Count, Layout Grid Toggle & Sort */}
          <div className="shop-toolbar__right">
            <span className="shop-toolbar__count">
              {apiProducts === null
                ? "…"
                : t("shop.showing", {
                    count: filteredAndSorted.length,
                    total: apiProducts.length,
                  })}
            </span>

            {/* Desktop Grid Switcher */}
            <div className="shop-toolbar__grid-toggle" aria-label="Toggle grid columns">
              <button
                type="button"
                onClick={() => setGridCols(4)}
                className={gridCols === 4 ? "active" : ""}
                title="4 Columns"
              >
                <Grid size={15} />
              </button>
              <button
                type="button"
                onClick={() => setGridCols(3)}
                className={gridCols === 3 ? "active" : ""}
                title="3 Columns"
              >
                <LayoutList size={15} />
              </button>
            </div>

            {/* Sort Dropdown */}
            <div className="shop-sort" ref={sortRef}>
              <button
                type="button"
                className="shop-sort__btn"
                onClick={() => setShowSort((prev) => !prev)}
                aria-haspopup="true"
                aria-expanded={showSort}
              >
                {t("action.sortBy")}{" "}
                <strong>{t(currentSort.labelKey)}</strong> <ChevronDown size={13} />
              </button>
              {showSort && (
                <div className="shop-sort__dropdown" role="menu">
                  {SORT_KEYS.map((s) => (
                    <button
                      key={s.key}
                      type="button"
                      role="menuitem"
                      className={s.key === sortBy ? "active" : ""}
                      onClick={() => {
                        setSortBy(s.key);
                        setShowSort(false);
                      }}
                    >
                      {t(s.labelKey)}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* ── Category Quick Pills Carousel ── */}
        <div className="shop-categories-bar">
          <div className="shop-categories-scroll">
            {filterCategories.map((c) => {
              const displayName =
                c.id === "all" || c.name === "All"
                  ? t("shop.all")
                  : (isRTL || language === "ar") && c.arabicName?.trim()
                  ? c.arabicName.trim()
                  : c.name;
              const isActive = activeCategory === c.name;
              return (
                <button
                  key={c.id}
                  type="button"
                  className={`shop-category-pill ${isActive ? "active" : ""}`}
                  onClick={() => setActiveCategory(c.name)}
                >
                  <span>{displayName}</span>
                  {c.productCount !== undefined && c.productCount > 0 && (
                    <span className="shop-category-pill__count">{c.productCount}</span>
                  )}
                </button>
              );
            })}
          </div>
        </div>

        {/* ── Active Filter Tags Bar ── */}
        {activeFiltersCount > 0 && (
          <div className="shop-active-filters">
            <span className="shop-active-filters__label">{t("shop.activeFilters")}</span>

            {activeCategory !== "All" && (
              <span className="shop-filter-chip">
                {activeCategoryTitle}
                <button
                  type="button"
                  onClick={() => setActiveCategory("All")}
                  aria-label={`Remove ${activeCategoryTitle} filter`}
                >
                  <X size={12} />
                </button>
              </span>
            )}

            {searchQuery.trim() && (
              <span className="shop-filter-chip">
                "{searchQuery}"
                <button
                  type="button"
                  onClick={() => setSearchQuery("")}
                  aria-label="Clear search filter"
                >
                  <X size={12} />
                </button>
              </span>
            )}

            {(priceRange.min || priceRange.max) && (
              <span className="shop-filter-chip">
                {priceRange.min ? `${priceRange.min} ${t("shop.egp")}` : "0"} –{" "}
                {priceRange.max ? `${priceRange.max} ${t("shop.egp")}` : "∞"}
                <button
                  type="button"
                  onClick={() => setPriceRange({ min: "", max: "" })}
                  aria-label="Clear price filter"
                >
                  <X size={12} />
                </button>
              </span>
            )}

            <button
              type="button"
              className="shop-active-filters__clear-all"
              onClick={resetAllFilters}
            >
              <RotateCcw size={12} /> {t("shop.clearAll")}
            </button>
          </div>
        )}

        {/* ── Products Grid / Skeletons ── */}
        {apiProducts === null ? (
          <div
            className="shop-grid"
            style={{ gridTemplateColumns: `repeat(${gridCols}, 1fr)` }}
          >
            {[...Array(8)].map((_, i) => (
              <div key={i} className="product-card-skeleton">
                <div className="product-card-skeleton__image" />
                <div className="product-card-skeleton__body">
                  <div className="product-card-skeleton__title" />
                  <div className="product-card-skeleton__price" />
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div
            className="shop-grid"
            style={{ gridTemplateColumns: `repeat(${gridCols}, 1fr)` }}
          >
            {filteredAndSorted.map((p) => (
              <ProductCard key={p.id} product={p} onAddToCart={onAddToCart} />
            ))}

            {/* Empty state */}
            {filteredAndSorted.length === 0 && !apiError && (
              <div className="shop-empty-state">
                <div className="shop-empty-state__icon">
                  <PackageSearch size={44} strokeWidth={1.2} />
                </div>
                <h3>{t("shop.noProductsTitle")}</h3>
                <p>{t("shop.noProductsHint")}</p>
                <button
                  type="button"
                  className="btn-primary shop-empty-state__reset-btn"
                  onClick={resetAllFilters}
                >
                  <RotateCcw size={14} /> {t("shop.resetFilters")}
                </button>
              </div>
            )}
          </div>
        )}

        {/* ── Luxury Brand Heritage Banner ── */}
        <div className="shop-luxury-banner">
          <div className="shop-luxury-banner__content">
            <span className="shop-luxury-banner__eyebrow">
              {t("shop.luxuryBannerEyebrow")}
            </span>
            <h3 className="shop-luxury-banner__title">
              {t("shop.luxuryBannerTitle")}
            </h3>
            <p className="shop-luxury-banner__desc">
              {t("shop.luxuryBannerSub")}
            </p>
            <Link to="/about" className="shop-luxury-banner__cta">
              <span>{t("shop.luxuryBannerCta")}</span>
              {isRTL ? <ArrowLeft size={16} /> : <ArrowRight size={16} />}
            </Link>
          </div>
        </div>
      </div>

      {/* ── Slide-Over Faceted Filter Drawer ── */}
      {showFilterDrawer && (
        <div
          className="shop-filter-drawer-overlay"
          role="dialog"
          aria-modal="true"
          aria-labelledby="filter-drawer-heading"
          onClick={(e) => {
            if (e.target === e.currentTarget) setShowFilterDrawer(false);
          }}
        >
          <div className="shop-filter-drawer">
            {/* Drawer Header */}
            <div className="shop-filter-drawer__header">
              <div className="shop-filter-drawer__title-wrap">
                <SlidersHorizontal size={18} />
                <h3 id="filter-drawer-heading">{t("shop.filterDrawerTitle")}</h3>
              </div>
              <button
                type="button"
                className="shop-filter-drawer__close"
                onClick={() => setShowFilterDrawer(false)}
                aria-label="Close filters"
              >
                <X size={18} />
              </button>
            </div>

            {/* Drawer Body */}
            <div className="shop-filter-drawer__body">
              {/* Category Filter Section */}
              <div className="shop-filter-section">
                <h4 className="shop-filter-section__title">{t("shop.categories")}</h4>
                <div className="shop-filter-categories-list">
                  {filterCategories.map((c) => {
                    const displayName =
                      c.id === "all" || c.name === "All"
                        ? t("shop.all")
                        : (isRTL || language === "ar") && c.arabicName?.trim()
                        ? c.arabicName.trim()
                        : c.name;
                    const isSelected = activeCategory === c.name;
                    return (
                      <button
                        key={c.id}
                        type="button"
                        className={`shop-filter-cat-option ${isSelected ? "selected" : ""}`}
                        onClick={() => setActiveCategory(c.name)}
                      >
                        <div className="shop-filter-cat-radio">
                          {isSelected && <span className="shop-filter-cat-radio__dot" />}
                        </div>
                        <span className="shop-filter-cat-name">{displayName}</span>
                        {c.productCount !== undefined && (
                          <span className="shop-filter-cat-count">({c.productCount})</span>
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Price Range Section */}
              <div className="shop-filter-section">
                <h4 className="shop-filter-section__title">{t("shop.priceRange")}</h4>

                {/* Quick Presets */}
                <div className="shop-filter-presets">
                  {PRICE_PRESETS.map((preset, idx) => {
                    const isMatch =
                      priceRange.min === preset.min && priceRange.max === preset.max;
                    return (
                      <button
                        key={idx}
                        type="button"
                        className={`shop-filter-preset-btn ${isMatch ? "active" : ""}`}
                        onClick={() =>
                          setPriceRange(
                            isMatch ? { min: "", max: "" } : { min: preset.min, max: preset.max }
                          )
                        }
                      >
                        {t(preset.labelKey)}
                      </button>
                    );
                  })}
                </div>

                {/* Custom Min/Max Inputs */}
                <div className="shop-price-inputs">
                  <div className="shop-price-input-wrap">
                    <label htmlFor="price-min">{t("shop.minPrice")}</label>
                    <div className="shop-price-input-field">
                      <input
                        id="price-min"
                        type="number"
                        min="0"
                        placeholder="0"
                        value={priceRange.min}
                        onChange={(e) =>
                          setPriceRange((prev) => ({ ...prev, min: e.target.value }))
                        }
                      />
                      <span className="shop-price-currency">{t("shop.egp")}</span>
                    </div>
                  </div>

                  <span className="shop-price-dash">—</span>

                  <div className="shop-price-input-wrap">
                    <label htmlFor="price-max">{t("shop.maxPrice")}</label>
                    <div className="shop-price-input-field">
                      <input
                        id="price-max"
                        type="number"
                        min="0"
                        placeholder="Max"
                        value={priceRange.max}
                        onChange={(e) =>
                          setPriceRange((prev) => ({ ...prev, max: e.target.value }))
                        }
                      />
                      <span className="shop-price-currency">{t("shop.egp")}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Sorting Section */}
              <div className="shop-filter-section">
                <h4 className="shop-filter-section__title">{t("action.sortBy")}</h4>
                <div className="shop-filter-sort-options">
                  {SORT_KEYS.map((s) => (
                    <button
                      key={s.key}
                      type="button"
                      className={`shop-filter-sort-btn ${sortBy === s.key ? "selected" : ""}`}
                      onClick={() => setSortBy(s.key)}
                    >
                      <span>{t(s.labelKey)}</span>
                      {sortBy === s.key && <Check size={14} className="selected-check" />}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Drawer Footer */}
            <div className="shop-filter-drawer__footer">
              <button
                type="button"
                className="btn-outline shop-filter-drawer__reset"
                onClick={resetAllFilters}
              >
                {t("shop.resetFilters")}
              </button>
              <button
                type="button"
                className="btn-primary shop-filter-drawer__apply"
                onClick={() => setShowFilterDrawer(false)}
              >
                {t("shop.applyFilters")} ({filteredAndSorted.length})
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
